// CB:BEGIN stripe-import
import { loadStripe } from '@stripe/stripe-js';
import { getApiUrl } from './getApiUrl';

// CB:BEGIN fix-tdz-stripe-key
// Read key at access time so Vite-inlined env is available (avoids "key not set" when .env loads after module)
function getPublishableKeyFromEnv(): string | undefined {
  if (typeof window !== 'undefined') {
    const w = window as any;
    if (w.VITE_STRIPE_PUBLISHABLE_KEY) return w.VITE_STRIPE_PUBLISHABLE_KEY;
  }
  return (import.meta as any)?.env?.VITE_STRIPE_PUBLISHABLE_KEY;
}
// CB:END fix-tdz-stripe-key

// CB:BEGIN stripe-https-dev
const allowHttp = (import.meta as any)?.env?.VITE_DEV_STRIPE_HTTP === 'true';
const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location?.hostname === 'localhost' || window.location?.hostname === '127.0.0.1');
const isBrowserSecure =
  typeof window !== 'undefined' &&
  (window.location?.protocol === 'https:' || allowHttp || isLocalhost);
// CB:END stripe-https-dev

// CB:BEGIN stripe-key-from-api
// Always fetch the publishable key from the backend — Railway is the single source of truth.
// The baked-in Vite env var may be stale (e.g. old pk_test_ from a previous Vercel build)
// so we prefer the API response and fall back to the env var only if the API is unreachable.
async function resolvePublishableKey(): Promise<string | undefined> {
  try {
    const res = await fetch(getApiUrl('/api/config/stripe'));
    if (res.ok) {
      const data = await res.json();
      if (data.publishableKey) {
        if (typeof window !== 'undefined') {
          (window as any).VITE_STRIPE_PUBLISHABLE_KEY = data.publishableKey;
        }
        const envKey = getPublishableKeyFromEnv();
        if (envKey && envKey !== data.publishableKey) {
          console.warn(`[Stripe] Overriding baked-in key (${envKey.substring(0, 20)}...) with backend key (${data.publishableKey.substring(0, 20)}...) — update VITE_STRIPE_PUBLISHABLE_KEY in Vercel to silence this`);
        }
        console.log(`[Stripe] Publishable key loaded from backend API (${data.mode} mode)`);
        return data.publishableKey;
      }
    }
  } catch (err) {
    console.warn('[Stripe] Could not reach /api/config/stripe, falling back to env var:', err);
  }
  // API unreachable — use whatever is baked in
  return getPublishableKeyFromEnv();
}
// CB:END stripe-key-from-api

// Lazy promise: key is resolved (env or API) when Stripe is first used
let _stripePromise: Promise<any> | null = null;
function getStripePromise(): Promise<any> {
  if (_stripePromise != null) return _stripePromise;
  _stripePromise = resolvePublishableKey().then((key) => {
    if (key) {
      const mode = key.startsWith('pk_live_') ? 'LIVE' : key.startsWith('pk_test_') ? 'TEST' : 'UNKNOWN';
      console.log(`[Stripe] Loading with publishable key: ${key.substring(0, 20)}... (${mode})`);
      return isBrowserSecure ? loadStripe(key) : null;
    }
    console.error('[Stripe] No publishable key available — set STRIPE_PUBLISHABLE_KEY in Railway (or VITE_STRIPE_PUBLISHABLE_KEY in Vercel)');
    return null;
  });
  return _stripePromise;
}

// Export a thenable so key is read on first .then() / use in <Elements>, not at import time
const stripePromise = {
  then(onFulfilled: any, onRejected?: any) {
    return getStripePromise().then(onFulfilled, onRejected);
  },
  catch(onRejected: any) {
    return getStripePromise().catch(onRejected);
  },
  finally(onFinally: any) {
    return getStripePromise().finally(onFinally);
  },
};

// CB:END stripe-import

export default stripePromise;
